import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { BarChart3, QrCode, Store, Target } from 'lucide-react';
export const Route = createFileRoute('/_layout/case-studies/beef-and-basil/')({
    component: CaseStudiesBeefAndBasilPage,
});
function CaseStudiesBeefAndBasilPage() {
    return (<main className="min-h-dvh bg-background">
            {/* Hero */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Case study</p>

                <h1 className="mt-2 text-3xl md:text-5xl font-bold">
                    Beef &amp; Basil: Measuring Offline Campaign Performance
                </h1>

                <p className="mt-4 max-w-2xl text-muted-foreground">
                    <strong>Beef &amp; Basil</strong> is a local restaurant that used{' '}
                    <Link to="/what-is-adora">Adora Analytics</Link> to understand how offline materials like menus and
                    flyers performed in the real world.
                </p>
            </section>

            <Separator />

            {/* Context snapshot */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <div className="grid gap-6 md:grid-cols-3">
                    <Snapshot icon={<Store className="h-5 w-5"/>} title="Business type" text="Local restaurant with in-store and takeaway traffic"/>
                    <Snapshot icon={<QrCode className="h-5 w-5"/>} title="Channels" text="Printed menus, packaging, in-store signage"/>
                    <Snapshot icon={<BarChart3 className="h-5 w-5"/>} title="Goal" text="Understand which offline materials drive engagement"/>
                </div>
            </section>

            {/* Problem */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">The problem</h2>

                <p className="mt-4 max-w-2xl text-muted-foreground">
                    Like many restaurants, Beef &amp; Basil relied heavily on physical touchpoints — printed menus,
                    takeaway packaging, and in-store signage. However, there was no clear way to measure which materials
                    actually drove customer actions.
                </p>

                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <li>• No visibility into which menu placements worked</li>
                    <li>• No data on flyer or QR engagement</li>
                    <li>• Decisions were based on intuition rather than evidence</li>
                </ul>
            </section>

            {/* Approach */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">The approach</h2>

                <p className="mt-4 max-w-2xl text-muted-foreground">
                    Beef &amp; Basil used Adora to treat each offline material as a measurable campaign instead of a
                    static asset.
                </p>

                <div className="mt-6 grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Target className="h-5 w-5 text-primary"/>
                                Campaign setup
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• Created separate campaigns for menus and placements</p>
                            <p>
                                • Used <Link to="/learn/smart-links">Smart Links</Link> and QR codes as entry points
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5 text-primary"/>
                                Measurement
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• Tracked scans and visits by placement</p>
                            <p>• Compared performance instead of guessing</p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* What was measured */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">What was measured</h2>

                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <li>• QR scans from menus and printed materials</li>
                    <li>• Unique vs total visits</li>
                    <li>• Performance differences between placements</li>
                </ul>
            </section>

            {/* Outcome */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">The outcome</h2>

                <p className="mt-4 max-w-2xl text-muted-foreground">
                    With offline data now visible, Beef &amp; Basil could identify which materials were actually driving
                    engagement and which ones were not.
                </p>

                <p className="mt-2 max-w-2xl text-muted-foreground">
                    This made it easier to focus effort on high-impact placements and remove underperforming ones.
                </p>

                <p className="mt-4 font-medium">The key result was clarity — not more complexity.</p>
            </section>

            {/* Reinforcement */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12">
                <Card>
                    <CardHeader>
                        <CardTitle>Learn more</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/learn/offline-marketing-analytics">
                                    Offline marketing analytics explained
                                </Link>
                            </li>
                            <li>
                                <Link to="/learn/smart-links">What are Smart Links?</Link>
                            </li>
                            <li>
                                <Link to="/how-it-works">How Adora works</Link>
                            </li>
                            <li>
                                <Link to="/pricing">View pricing</Link>
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </section>
        </main>);
}
const Snapshot = ({ icon, title, text }) => {
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {icon}
                    </span>
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{text}</CardContent>
        </Card>);
};
Snapshot.displayName = 'Snapshot';
